<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { CampusProductMinimal, UserProfileUpdateDTO, UserRegistrationListItem } from '@/types'

import { useAppToast } from '@/composables/useAppToast'
import { registrationStatusColor } from '@/composables/useRegistrationStatusColor'
import campusService from '@/services/campusService'
import userService from '@/services/userService'

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { userInfo, fetchUserInfo, logout } = useUser()
const toast = useAppToast()
const { t, locale } = useI18n()
const router = useRouter()
// Local state for form fields
const firstName = ref<string | null>(null)
const lastName = ref<string | null>(null)
const email = ref<string | null>(null)
const externalProduct = ref<string | null>(null)
const matriculationId = ref<string | null>(null)
const department = ref<string | null>(null)
const plannedProductId = ref<string | null>(null)
const plannedProductName = ref<string | null>(null)
const originalEmail = ref<string | null>(null) // To track email changes
const campusProducts = ref<CampusProductMinimal[]>([])
const campusProductsLoading = ref(false)

const loading = ref(false) // Loading state for profile save
const deleteLoading = ref(false) // Loading state for account deletion

// Delete dialog state
const deleteDialogVisible = ref(false)
const registrations = ref<UserRegistrationListItem[]>([])
const registrationsLoading = ref(false)
const registrationsError = ref(false)

const isExternalUser = computed(() => userInfo.value?.userType === 'external')
// Students, employees and partners have their name/email managed centrally via IDM
const isManagedByIdm = computed(
  () =>
    userInfo.value?.userType === 'student' ||
    userInfo.value?.userType === 'employee' ||
    userInfo.value?.userType === 'partner',
)

// Computed property to check if email has changed
const emailChanged = computed(() => email.value !== originalEmail.value)

// Function to populate form from store data
const populateForm = () => {
  if (userInfo.value) {
    firstName.value = userInfo.value.firstName ?? ''
    lastName.value = userInfo.value.lastName ?? ''
    email.value = userInfo.value.mail ?? ''
    originalEmail.value = userInfo.value.mail ?? ''
    // Populate external fields only if they exist in the profile (might not always)
    externalProduct.value = userInfo.value?.externalProduct ?? ''
    matriculationId.value = userInfo.value?.matriculationId ?? ''
    department.value = userInfo.value?.department ?? ''
    plannedProductId.value = userInfo.value?.plannedProductId ?? ''
    plannedProductName.value = userInfo.value?.plannedProductName ?? ''
  }
}

const syncPlannedProductName = () => {
  if (!plannedProductId.value) {
    plannedProductName.value = ''
    return
  }

  const selected = campusProducts.value.find((product) => product.id === plannedProductId.value)
  if (selected) {
    plannedProductName.value = selected.name
  }
}

const loadCampusProducts = async () => {
  campusProductsLoading.value = true
  try {
    campusProducts.value = await campusService.getProducts()
    syncPlannedProductName()
  } catch (error) {
    console.error('Failed to load campus products:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.user.settings.profile.productsLoadError'),
      duration: 5000,
    })
  } finally {
    campusProductsLoading.value = false
  }
}

// Function to reset form to original values from store
const resetForm = () => {
  populateForm()
}

// Fetch profile data on component mount (or rely on store being populated already)
onMounted(() => {
  // If store might not be populated, fetch it first
  // await userStore.fetchUserProfile(); // Uncomment if needed
  populateForm()
  void loadCampusProducts()
})

// Watch for changes in the store user info (e.g., after login) and update form
watch(
  () => userInfo.value,
  (newInfo) => {
    // Debug: User info updated in store
    if (newInfo) {
      populateForm()
    }
  },
  { deep: true },
)

watch([plannedProductId, campusProducts], () => {
  syncPlannedProductName()
})

// Function to save the profile
const saveProfile = async () => {
  loading.value = true
  try {
    const profileData: UserProfileUpdateDTO = {
      ...(!isManagedByIdm.value && {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
      }),
      ...(isExternalUser.value && {
        department: department.value,
        plannedProductId: plannedProductId.value,
        plannedProductName: plannedProductName.value,
        externalProduct: externalProduct.value,
        matriculationId: matriculationId.value,
      }),
    }
    await userService.updateUserProfile(profileData)
    fetchUserInfo(true) // Refresh user info in store after update

    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.user.settings.profile.updateSuccess'),
      duration: 3000,
    })
  } catch (error: any) {
    console.error('Failed to update profile:', error)
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.title ||
      t('pages.user.settings.profile.updateError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000, // Show error longer
    })
  } finally {
    loading.value = false
  }
}

const eventName = (registration: UserRegistrationListItem) => {
  return locale.value === 'en'
    ? registration.eventNameEN || registration.eventNameDE
    : registration.eventNameDE
}

// Function to open delete dialog and load registrations
const handleDeleteAccount = async () => {
  deleteDialogVisible.value = true
  registrationsLoading.value = true
  registrationsError.value = false

  try {
    registrations.value = await userService.getRegistrations()
  } catch (err) {
    console.error('Failed to load registrations:', err)
    registrationsError.value = true
  } finally {
    registrationsLoading.value = false
  }
}

// Function to confirm and execute account deletion
const confirmDeleteAccount = async () => {
  deleteLoading.value = true
  try {
    const success = await userService.deleteAccount()

    if (success) {
      deleteDialogVisible.value = false
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.user.settings.profile.dangerZone.deleteSuccess'),
        duration: 5000,
      })
      await logout()
      router.push({ path: '/user/login' })
    } else {
      throw new Error(t('pages.user.settings.profile.dangerZone.deleteErrorUnknown'))
    }
  } catch (error: any) {
    console.error('Failed to delete account:', error)
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.title ||
      t('pages.user.settings.profile.dangerZone.deleteError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: errorMessage,
      duration: 5000,
    })
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <UCard>
    <template #header>
      <div class="flex items-center justify-between">
        <span>{{ t('pages.user.settings.profile.title') }}</span>
        <AppSpinner v-if="loading" style="width: 30px; height: 30px" />
      </div>
    </template>
    <template #default>
      <div class="max-w-180">
        <p class="mb-6">
          {{ t('pages.user.settings.profile.description') }}
        </p>

        <div class="flex flex-col gap-4">
          <AppAlert v-if="isManagedByIdm" color="info" :closable="false">
            {{ t('pages.user.settings.profile.managedByIdm') }}
          </AppAlert>

          <div class="flex flex-row gap-2">
            <AppFloatField variant="on" class="flex-1">
              <UInput
                id="firstname"
                v-model.nullable="firstName"
                :disabled="loading || isManagedByIdm"
                class="w-full"
                :color="!firstName ? 'error' : 'neutral'"
                :highlight="!firstName"
              />
              <label for="firstname">
                {{ t('fields.user.firstName') }}
                <sup>*</sup>
              </label>
            </AppFloatField>

            <AppFloatField variant="on" class="flex-1">
              <UInput
                id="lastname"
                v-model.nullable="lastName"
                :disabled="loading || isManagedByIdm"
                class="w-full"
                :color="!lastName ? 'error' : 'neutral'"
                :highlight="!lastName"
              />
              <label for="lastname">
                {{ t('fields.user.lastName') }}
                <sup>*</sup>
              </label>
            </AppFloatField>
          </div>
          <AppFloatField variant="on">
            <UInput
              id="email"
              v-model.nullable="email"
              type="email"
              :disabled="loading || isManagedByIdm"
              class="w-full"
              :color="!email || !email.includes('@') ? 'error' : 'neutral'"
              :highlight="!email || !email.includes('@')"
            />
            <label for="email">{{ t('fields.user.email') }}</label>
          </AppFloatField>
          <div
            v-if="emailChanged && !isManagedByIdm"
            class="text-sm text-orange-700 dark:text-orange-400"
          >
            {{ t('pages.user.settings.profile.emailChangeWarning') }}
          </div>

          <AppFloatField variant="on">
            <UInput
              id="department"
              v-model.nullable="department"
              :disabled="loading || !isExternalUser"
              class="w-full"
            />
            <label for="department">
              {{ t('fields.user.department') }}
              ({{ t('pages.user.settings.profile.optionalField') }})
            </label>
          </AppFloatField>

          <AppFloatField variant="on">
            <AppSelect
              id="plannedProduct"
              v-model="plannedProductId"
              :options="campusProducts"
              option-label="name"
              option-value="id"
              :disabled="loading || !isExternalUser"
              :loading="campusProductsLoading"
              filter
              show-clear
              class="w-full"
            />
            <label for="plannedProduct">
              {{ t('fields.user.plannedProductName') }}
              ({{ t('pages.user.settings.profile.optionalField') }})
            </label>
          </AppFloatField>

          <!-- Conditional Fields for External Role -->
          <template v-if="isExternalUser">
            <AppFloatField variant="on" class="mt-3">
              <UInput
                id="externalProduct"
                v-model.nullable="externalProduct"
                :disabled="loading"
                class="w-full"
              />
              <label for="externalProduct">{{ t('fields.user.externalProduct') }}</label>
            </AppFloatField>

            <AppFloatField variant="on">
              <UInput
                id="matriculationId"
                v-model.nullable="matriculationId"
                :disabled="loading"
                class="w-full"
              />
              <label for="matriculationId">{{ t('fields.user.matriculationId') }}</label>
            </AppFloatField>
          </template>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex max-w-180 justify-end gap-2">
        <UButton
          :label="t('common.actions.cancel')"
          color="neutral"
          :disabled="loading"
          @click="resetForm"
        />
        <UButton
          :label="t('common.actions.save')"
          icon="i-lucide-check"
          :loading="loading"
          @click="saveProfile"
        />
      </div>
    </template>
  </UCard>

  <!-- Danger Zone -->
  <USeparator class="my-6" />
  <UCard>
    <template #header>
      <span class="text-red-700 dark:text-red-400">{{
        t('pages.user.settings.profile.dangerZone.title')
      }}</span>
    </template>
    <template #default>
      <div class="max-w-180">
        <p class="mb-4">
          {{ t('pages.user.settings.profile.dangerZone.description') }}
        </p>
        <UButton
          :label="t('pages.user.settings.profile.dangerZone.deleteAccountButton')"
          color="error"
          icon="i-lucide-trash"
          :loading="deleteLoading"
          @click="handleDeleteAccount"
        />
      </div>
    </template>
  </UCard>

  <AppModal
    v-model:open="deleteDialogVisible"
    :title="t('pages.user.settings.profile.dangerZone.confirmDeleteHeader')"
    modal
    class="max-w-125"
    :closable="!deleteLoading"
    :close-on-escape="!deleteLoading"
  >
    <div class="flex flex-col gap-4">
      <p>{{ t('pages.user.settings.profile.dangerZone.confirmDelete') }}</p>

      <!-- Registration list -->
      <div v-if="registrationsLoading" class="flex items-center gap-2 text-muted">
        <AppSpinner style="width: 20px; height: 20px" />
        <span>{{ t('pages.user.settings.profile.dangerZone.loadingRegistrations') }}</span>
      </div>

      <AppAlert v-else-if="registrationsError" color="warning" :closable="false">
        {{ t('pages.user.settings.profile.dangerZone.loadRegistrationsError') }}
      </AppAlert>

      <div v-else-if="registrations.length === 0" class="text-sm text-muted">
        {{ t('pages.user.settings.profile.dangerZone.noRegistrations') }}
      </div>

      <div v-else>
        <p class="mb-2 text-sm font-semibold">
          {{ t('pages.user.settings.profile.dangerZone.registrationsWarning') }}
        </p>
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
          <li
            v-for="reg in registrations"
            :key="reg.registrationId"
            class="flex items-center justify-between gap-3 rounded-md border border-neutral-200 px-3 py-2 dark:border-neutral-700"
          >
            <span class="text-sm">{{ eventName(reg) }}</span>
            <UBadge :color="registrationStatusColor(reg.status)" class="shrink-0">
              {{ t(`enums.registrationStatus.${reg.status}`) }}
            </UBadge>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <UButton
          :label="t('common.actions.cancel')"
          color="neutral"
          :disabled="deleteLoading"
          @click="deleteDialogVisible = false"
          variant="ghost"
        />
        <UButton
          :label="t('common.actions.delete')"
          color="error"
          :loading="deleteLoading"
          @click="confirmDeleteAccount"
        />
      </div>
    </template>
  </AppModal>
</template>
